import { useId } from "react";

import { REQUEST_COPY } from "./representations.constants.ts";
import { useRequestRepresentationController } from "./useRequestRepresentationController.ts";

import "./representations.css";

/**
 * An agent asking a work's owner for a mandate.
 *
 * The one screen in ONDO reached by somebody who holds nothing at all about its
 * subject, which is why it takes two facts and not one: the code identifies the
 * work, and the artist's name or the year confirms the reader has really been
 * shown it. The code is opaque and carries neither, so the second field is a
 * second fact rather than a second copy of the first.
 *
 * NO CHECK-AS-YOU-TYPE, unlike `AssignAgentDialog` a screen over. That one asks
 * `POST /v1/eligibility` on blur because a collector already holds the subject
 * and is owed a usable answer about an id they were given. Here the caller holds
 * nothing, so an incremental check would be a code oracle — and the refusal
 * below is deliberately one sentence covering everything that can be wrong.
 *
 * @param principalOndoId whose request this is: the firm on an organisation's
 * workspace, the reader on a personal one. It decides who would hold the
 * mandate, and SQL checks the caller is an `admin` of it.
 */
export function RequestRepresentation({
  principalOndoId,
  canAsk = true,
}: {
  principalOndoId: string;
  /**
   * Admin, where the ask is made AS a firm.
   *
   * `ondo.request_representation(p_as_ondo_id)` requires admin on the principal
   * being asked for: approaching a collector in the firm's name commits the
   * firm, which is not a viewer's or an editor's to do. The form rendered and
   * submitted for any member before this, and the rung was checked only inside
   * the RPC — so a junior seat typed a code, a second fact and a note, pressed
   * Send, and got a refusal for a reason none of the fields was about.
   *
   * Defaults true for a personal workspace, where an independent advisor asks
   * as themselves and there is no firm to commit.
   */
  canAsk?: boolean;
}) {
  const { form, made, withdrawing, setCode, setConfirm, setNote, submit, withdraw } =
    useRequestRepresentationController(principalOndoId);

  const codeId = useId();
  const confirmId = useId();
  const noteId = useId();
  const helpId = useId();

  return (
    <section className="request">
      <p className="request__lead">
        <span className="request__lead-mark" aria-hidden="true">
          ◉
        </span>
        <span>{REQUEST_COPY.lead}</span>
      </p>

      {/* The form is REPLACED rather than disabled, unlike the Sell control.
          A disabled three-field form with a disabled button says nothing about
          why; a sentence naming whose ask it is says the whole thing, and there
          is nothing on this screen for a junior seat to read instead. What they
          asked for already is still listed below. */}
      {!canAsk && <p className="request__note">{REQUEST_COPY.needsAdmin}</p>}

      {canAsk && (
      <form
        className="request__card"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="request__field">
          <label className="request__label" htmlFor={codeId}>
            {REQUEST_COPY.codeLabel}
          </label>
          <input
            id={codeId}
            className="request__input request__input--code"
            value={form.code}
            placeholder={REQUEST_COPY.codePlaceholder}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setCode(event.target.value)}
          />
        </div>

        <div className="request__field">
          <label className="request__label" htmlFor={confirmId}>
            {REQUEST_COPY.confirmLabel}
          </label>
          <input
            id={confirmId}
            className="request__input"
            value={form.confirm}
            placeholder={REQUEST_COPY.confirmPlaceholder}
            autoComplete="off"
            aria-describedby={helpId}
            onChange={(event) => setConfirm(event.target.value)}
          />
          <p className="request__help" id={helpId}>
            {REQUEST_COPY.confirmHelp}
          </p>
        </div>

        <div className="request__field">
          <label className="request__label" htmlFor={noteId}>
            {REQUEST_COPY.noteLabel}
          </label>
          <textarea
            id={noteId}
            className="request__note"
            rows={2}
            value={form.note}
            placeholder={REQUEST_COPY.notePlaceholder}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>

        <div className="request__actions">
          <button type="submit" className="request__submit" disabled={!form.canSubmit}>
            {form.busy ? REQUEST_COPY.sending : REQUEST_COPY.submit}
          </button>
        </div>

        {/* `alert` on a refusal and `status` on a success, so a screen reader is
            interrupted only by the one that needs correcting. */}
        {form.outcome && (
          <p
            className={`request__outcome ${
              form.outcome.kind === "refused" ? "request__outcome--refused" : ""
            }`}
            role={form.outcome.kind === "refused" ? "alert" : "status"}
          >
            {form.outcome.message}
          </p>
        )}
      </form>
      )}

      <section className="request__card">
        <h2 className="request__made-title">{REQUEST_COPY.madeTitle}</h2>

        {made.kind === "loading" && <p className="request__empty" role="status">{made.message}</p>}

        {made.kind === "error" && <p className="request__empty" role="alert">{made.message}</p>}

        {made.kind === "empty" && <p className="request__empty">{made.message}</p>}

        {made.kind === "rows" && (
          <ul className="request__list">
            {made.rows.map((row) => (
              <li key={row.requestId} className="request__row">
                <div className="request__row-body">
                  <p className="request__row-title">
                    {row.title} — {row.attributedName}
                  </p>
                  <p className="request__row-meta">
                    <span className="request__code">{row.ondoCode}</span>
                    {row.responseNote ? ` · ${row.responseNote}` : ""}
                  </p>
                </div>

                <span
                  className={`request__stamp ${row.open ? "request__stamp--open" : ""}`}
                >
                  {row.status}
                </span>

                {/* Offered only when the database said so. `canWithdraw` is
                    computed from the same set the withdraw admits, so a junior
                    member of a firm sees no control that would be refused. */}
                {row.canWithdraw && (
                  <button
                    type="button"
                    className="request__withdraw"
                    disabled={withdrawing !== null}
                    onClick={() => withdraw(row.requestId)}
                  >
                    {withdrawing === row.requestId
                      ? REQUEST_COPY.withdrawing
                      : REQUEST_COPY.withdraw}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}
